/**
 * Cognito Hosted UI PKCE auth core — config-driven factory.
 * Uses Web Crypto API only — no new npm packages.
 *
 * Extracted from apps/operations/src/lib/auth.ts and apps/customer/src/lib/auth.ts
 * (RUF-349/RUF-393), which were ~180 lines byte-identical. Each portal instantiates
 * its own client with its own Cognito pool config and storage-key prefix; this
 * module contains only the PKCE/token primitives, not portal-specific identity
 * logic (e.g. customer's DB-derived ClientIdentity, ops's cognito:groups reading).
 */

export interface AuthClientConfig {
  /** Which token this app's API calls need — operations: 'access', customer: 'id'. */
  tokenKind: 'access' | 'id';
  /** Storage-key namespace, e.g. 'sx' vs 'sx_customer'. Must be distinct per instance. */
  storageKeyPrefix: string;
  cognitoDomain: string;
  clientId: string;
  redirectUri: string;
}

export interface AuthClient {
  buildLoginUrl(): Promise<string>;
  exchangeCodeForTokens(code: string): Promise<boolean>;
  refreshAccessToken(): Promise<boolean>;
  getAccessToken(): string | null;
  getIdToken(): string | null;
  getRefreshToken(): string | null;
  /** Returns whichever token `tokenKind` selects — the credential 401-retry and the route guard key off of. */
  getPrimaryToken(): string | null;
  isTokenExpired(token: string): boolean;
  clearTokens(): void;
  /** Clears tokens, invokes `onCleanup` (e.g. a customer identity cache), then redirects to Cognito logout. */
  signOut(onCleanup?: () => void): void;
  /** Route-guard: silently refresh if the session is expired, else redirect to Cognito Hosted UI and block forever. */
  silentRefreshOrRedirect(): Promise<void>;
  /**
   * Remember the originally-requested path so the post-login callback can
   * restore it instead of always landing on the app's default route. Only
   * accepts a same-origin path starting with a single `/` (rejects `//...`
   * or anything containing `://`, to avoid an open redirect) — silently
   * no-ops otherwise. Session-scoped, same rationale as the PKCE verifier.
   */
  stashReturnTo(path: string): void;
  /** Read-then-delete the stashed return-to path (single use). Returns null if absent. */
  consumeReturnTo(): string | null;
}

/** Generic JWT payload decoder — each app builds its own one-line claim extractor on top (getUserGroups, getEmail, etc). */
export function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    const [, payload] = token.split('.');
    if (!payload) return {};
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return {};
  }
}

// Only tokenKind/storageKeyPrefix are validated eagerly at construction time —
// they're structural (used to build storage keys immediately) and a mixup
// between two instances is a silent, hard-to-debug cross-portal token leak.
// cognitoDomain/clientId/redirectUri are just as required, but validating
// them eagerly means any import of this module without real Cognito config
// present (e.g. a unit test that never calls a network-touching method)
// throws at import time — they fail loudly enough on first actual use
// (a request to `undefined/oauth2/token` errors immediately).
function assertConfig(config: AuthClientConfig): void {
  if (!config.tokenKind) throw new Error('createAuthClient: tokenKind is required');
  if (!config.storageKeyPrefix) throw new Error('createAuthClient: storageKeyPrefix is required');
}

async function generateCodeVerifier(): Promise<string> {
  const array = crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

export function createAuthClient(config: AuthClientConfig): AuthClient {
  assertConfig(config);

  const storageKeys = {
    accessToken: `${config.storageKeyPrefix}_access_token`,
    idToken: `${config.storageKeyPrefix}_id_token`,
    // RUF-337: refresh token is no longer written to any web storage; kept here
    // only so clearTokens() can evict any stale value left by older code versions.
    refreshToken: `${config.storageKeyPrefix}_refresh_token`,
    codeVerifier: `${config.storageKeyPrefix}_pkce_verifier`,
    returnTo: `${config.storageKeyPrefix}_return_to`,
  } as const;

  // RUF-337: held in a per-instance closure variable (never module-level) so two
  // client instances with different storageKeyPrefixes never share or clobber
  // each other's refresh token, and it's never written to sessionStorage or
  // localStorage — neither is safe from XSS exfil. Trade-off: lost on page
  // reload (forces re-auth), the correct posture for a short-lived session.
  let _refreshToken: string | null = null;

  // In-flight verifier generation, shared across concurrent callers (e.g. a
  // route-guard beforeLoad and an authFetch redirect firing in the same tick).
  // Without this, two concurrent buildLoginUrl() calls each see an empty
  // sessionStorage synchronously, each generate their OWN verifier, and each
  // compute a challenge from their own value — the last write to storage wins,
  // but the URL(s) already handed back may carry a challenge that doesn't match
  // what's actually stored, breaking the token exchange.
  let pendingVerifier: Promise<string> | null = null;

  // In-flight/completed exchangeCodeForTokens() call for the current code,
  // shared across concurrent AND sequential callers with the same code.
  // SIL-1339: the callback route's effect can run exchangeCodeForTokens twice
  // for the same authorization code (React re-invoking the effect, or the
  // callback route re-rendering before navigating away). Without this guard,
  // both invocations read the verifier from sessionStorage and race: whichever
  // resolves first deletes the verifier on success, so the second either
  // re-POSTs the single-use code (Cognito rejects it) or — if it reads storage
  // after the first has already cleared it — logs the spurious
  // "no code verifier in storage" error even though the session's sign-in
  // ultimately succeeded. Keyed by code (not just a bare flag) so a *second*,
  // distinct code — a genuine new login — is never blocked by a stale result.
  let exchangeInFlight: { code: string; result: Promise<boolean> } | null = null;

  // In-flight silentRefreshOrRedirect(), shared across concurrent callers (e.g.
  // TanStack Router's intent-preload re-running beforeLoad on link hover ahead
  // of the real navigation's beforeLoad, or — in the customer app — an
  // onAuthExpired callback firing alongside a route guard). Without this,
  // each independent caller sees no valid access token and independently
  // calls buildLoginUrl() + window.location.assign(), racing two full-page
  // redirects to Cognito against each other (observed as NS_BINDING_ABORTED
  // in Firefox, with a new `code=` param flashing on each attempt).
  //
  // Deliberately NOT reset when the redirect branch is taken: that branch
  // blocks forever on a promise that never resolves, so there's no page left
  // to reset it on. It IS reset after a normal resolving completion
  // (already-valid token, or a successful silent refresh) so a later real
  // expiry is re-checked correctly. `.finally()` gives us exactly this split
  // for free, since `.finally` never fires on a promise that never settles.
  let redirectInFlight: Promise<void> | null = null;

  function getOrCreateVerifier(): Promise<string> {
    const stored = sessionStorage.getItem(storageKeys.codeVerifier);
    if (stored) return Promise.resolve(stored);
    if (!pendingVerifier) {
      pendingVerifier = generateCodeVerifier().then((v) => {
        sessionStorage.setItem(storageKeys.codeVerifier, v);
        return v;
      });
      pendingVerifier.finally(() => {
        pendingVerifier = null;
      });
    }
    return pendingVerifier;
  }

  function getAccessToken(): string | null {
    return sessionStorage.getItem(storageKeys.accessToken);
  }

  function getIdToken(): string | null {
    return sessionStorage.getItem(storageKeys.idToken);
  }

  function getRefreshToken(): string | null {
    return _refreshToken;
  }

  function getPrimaryToken(): string | null {
    return config.tokenKind === 'access' ? getAccessToken() : getIdToken();
  }

  function isTokenExpired(token: string): boolean {
    const { exp } = decodeJwtPayload(token) as { exp?: number };
    if (!exp) return true;
    return Date.now() / 1000 > exp - 30;
  }

  function clearTokens(): void {
    _refreshToken = null;
    Object.values(storageKeys).forEach((k) => {
      sessionStorage.removeItem(k);
      localStorage.removeItem(k);
    });
  }

  async function buildLoginUrl(): Promise<string> {
    // RUF-339: code_verifier is single-use and short-lived — sessionStorage is
    // the correct scope (tab-scoped, cleared on tab close, not persisted to disk).
    const verifier = await getOrCreateVerifier();
    const challenge = await generateCodeChallenge(verifier);

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: config.clientId,
      redirect_uri: config.redirectUri,
      scope: 'openid email profile',
      code_challenge_method: 'S256',
      code_challenge: challenge,
    });

    return `${config.cognitoDomain}/oauth2/authorize?${params.toString()}`;
  }

  async function exchangeCodeForTokens(code: string): Promise<boolean> {
    // See exchangeInFlight's declaration above (SIL-1339): a second call with
    // the SAME code — concurrent or sequential — reuses the one real exchange
    // instead of racing it or re-reading a verifier the first call already
    // consumed. A call with a DIFFERENT code (a genuinely new login) always
    // starts fresh.
    if (exchangeInFlight && exchangeInFlight.code === code) {
      return exchangeInFlight.result;
    }

    const result = doExchange(code);
    exchangeInFlight = { code, result };
    return result;
  }

  async function doExchange(code: string): Promise<boolean> {
    const verifier = sessionStorage.getItem(storageKeys.codeVerifier);
    if (!verifier) {
      console.error('[auth] token exchange: no code verifier in storage');
      return false;
    }

    try {
      const res = await fetch(`${config.cognitoDomain}/oauth2/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: config.clientId,
          redirect_uri: config.redirectUri,
          code,
          code_verifier: verifier,
        }).toString(),
      });

      if (!res.ok) {
        const body = await res.text().catch(() => '');
        console.error('[auth] token exchange failed', res.status, body);
        sessionStorage.removeItem(storageKeys.codeVerifier);
        return false;
      }

      const tokens = await res.json();
      sessionStorage.setItem(storageKeys.accessToken, tokens.access_token);
      sessionStorage.setItem(storageKeys.idToken, tokens.id_token);
      // RUF-337: store refresh token in memory, not sessionStorage, to reduce XSS exfil surface.
      if (tokens.refresh_token) {
        _refreshToken = tokens.refresh_token;
      }
      sessionStorage.removeItem(storageKeys.codeVerifier);
      return true;
    } catch {
      sessionStorage.removeItem(storageKeys.codeVerifier);
      return false;
    }
  }

  async function refreshAccessToken(): Promise<boolean> {
    const refreshToken = _refreshToken;
    if (!refreshToken) return false;

    try {
      const res = await fetch(`${config.cognitoDomain}/oauth2/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: config.clientId,
          refresh_token: refreshToken,
        }).toString(),
      });

      if (!res.ok) return false;

      const tokens = await res.json();
      sessionStorage.setItem(storageKeys.accessToken, tokens.access_token);
      if (tokens.id_token) {
        sessionStorage.setItem(storageKeys.idToken, tokens.id_token);
      }
      return true;
    } catch {
      return false;
    }
  }

  function stashReturnTo(path: string): void {
    // Reject anything that isn't a single-leading-slash, same-origin path —
    // `//host/evil` is protocol-relative and `scheme://host/evil` is
    // absolute; either could redirect off-origin if this value is ever
    // consumed by something less careful than a same-origin router navigate().
    if (!path.startsWith('/') || path.startsWith('//') || path.includes('://')) return;
    sessionStorage.setItem(storageKeys.returnTo, path);
  }

  function consumeReturnTo(): string | null {
    const value = sessionStorage.getItem(storageKeys.returnTo);
    if (value === null) return null;
    sessionStorage.removeItem(storageKeys.returnTo);
    return value;
  }

  function signOut(onCleanup?: () => void): void {
    clearTokens();
    onCleanup?.();
    const domain = config.cognitoDomain.replace(/^https?:\/\//, '');
    const logoutUrl = new URL(`https://${domain}/logout`);
    logoutUrl.searchParams.set('client_id', config.clientId);
    logoutUrl.searchParams.set('logout_uri', window.location.origin + '/');
    window.location.href = logoutUrl.toString();
  }

  function silentRefreshOrRedirect(): Promise<void> {
    if (redirectInFlight) return redirectInFlight;

    redirectInFlight = (async () => {
      // Matches the pre-extraction beforeLoad logic in both apps' __root.tsx exactly:
      // the expiry gate checks the access token specifically (Cognito issues/expires
      // access + ID tokens together in the same response, so this is equivalent for
      // both tokenKind configurations) rather than the configured primary token.
      let accessToken = getAccessToken();

      if (!accessToken || isTokenExpired(accessToken)) {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          accessToken = getAccessToken();
        } else {
          const loginUrl = await buildLoginUrl();
          window.location.assign(loginUrl);
          // Block routing indefinitely while browser navigates away. Never
          // resolves, so the .finally() below never fires — redirectInFlight
          // intentionally stays set for the rest of this page's lifetime.
          await new Promise<never>(() => {});
        }
      }
    })();

    redirectInFlight.finally(() => {
      redirectInFlight = null;
    });

    return redirectInFlight;
  }

  return {
    buildLoginUrl,
    exchangeCodeForTokens,
    refreshAccessToken,
    getAccessToken,
    getIdToken,
    getRefreshToken,
    getPrimaryToken,
    isTokenExpired,
    clearTokens,
    signOut,
    silentRefreshOrRedirect,
    stashReturnTo,
    consumeReturnTo,
  };
}

// Exported for unit testing only (verifier format / S256 correctness).
export const __testing = { generateCodeVerifier, generateCodeChallenge };
